import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { Baby, BarChart3, Brain, Check, CheckCheck, ChevronLeft, ClipboardList, Clock, Eye, FileText, Home, MessageCircle, Phone, Plus, RefreshCw, Search, Send, Shield, User, UserCheck, Users, X } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalChat = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const messagesEndRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [initialMessage, setInitialMessage] = useState('');

  const getToken = () => localStorage.getItem('principal_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadConversations();
  }, [navigate]);

  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/chat/conversations`, {
        params: { token, user_type: 'principal' }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (convId) => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/chat/conversation/${convId}`, {
        params: { token }
      });
      setSelectedConversation(response.data.conversation);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const loadParents = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/principal/chat/parents`, {
        params: { token, search: searchQuery }
      });
      setParents(response.data.parents || []);
    } catch (error) {
      console.error('Error loading parents:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/chat/message`, null, {
        params: {
          token,
          conversation_id: selectedConversation.id,
          content: newMessage
        }
      });
      setNewMessage('');
      loadConversation(selectedConversation.id);
      loadConversations(); // Refresh conversation list
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const startNewChat = async () => {
    if (!selectedParent) return;
    
    setSending(true);
    try {
      const token = getToken();
      const response = await axios.post(`${BACKEND_URL}/api/principal/chat/start`, null, {
        params: {
          token,
          parent_id: selectedParent.id,
          child_id: selectedParent.children?.[0]?.id,
          initial_message: initialMessage || null
        }
      });
      
      setShowNewChatModal(false);
      setSelectedParent(null);
      setInitialMessage('');
      loadConversations();
      
      // Navigate to new conversation
      if (response.data.conversation) {
        navigate(`/principal/chat/${response.data.conversation.id}`);
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      alert('Failed to start chat');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.other_participant?.name?.toLowerCase().includes(query) ||
      conv.child_name?.toLowerCase().includes(query) ||
      conv.last_message?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="principal-chat-page">
      
      
      <div className="pt-16 h-screen flex">
        {/* Conversations List */}
        <div className="w-80 bg-white border-r flex flex-col">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Messages</h2>
              <Button 
                size="sm" 
                onClick={() => {
                  setShowNewChatModal(true);
                  loadParents();
                }}
                data-testid="new-chat-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-slate-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No conversations yet</p>
                <Button 
                  variant="link" 
                  onClick={() => {
                    setShowNewChatModal(true);
                    loadParents();
                  }}
                >
                  Start a conversation
                </Button>
              </div>
            ) : (
              filteredConversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => navigate(`/principal/chat/${conv.id}`)}
                  className={`p-4 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedConversation?.id === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                      {conv.other_participant?.name?.charAt(0) || 'P'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-slate-800 truncate">
                          {conv.other_participant?.name || 'Unknown'}
                        </p>
                        <span className="text-xs text-slate-500">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                      {conv.child_name && (
                        <p className="text-xs text-sanjaya-navy flex items-center gap-1">
                          <Baby className="w-3 h-3" />
                          {conv.child_name}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 truncate">
                        {conv.last_message || 'No messages yet'}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 bg-white border-b flex items-center gap-4">
                <button 
                  onClick={() => {
                    setSelectedConversation(null);
                    navigate('/principal/chat');
                  }}
                  className="lg:hidden"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                  {selectedConversation.participant_2_name?.charAt(0) || 'P'}
                </div>
                <div>
                  <p className="font-semibold">{selectedConversation.participant_2_name}</p>
                  <p className="text-sm text-slate-500">{selectedConversation.subject}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-100">
                {messages.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map(msg => {
                    const isMe = msg.sender_type === 'principal';
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${isMe ? 'order-2' : ''}`}>
                          <div
                            className={`p-3 rounded-2xl ${
                              isMe 
                                ? 'bg-blue-500 text-white rounded-br-md' 
                                : 'bg-white text-slate-800 rounded-bl-md shadow-sm'
                            }`}
                          >
                            <p>{msg.content}</p>
                          </div>
                          <div className={`flex items-center gap-1 mt-1 text-xs text-slate-500 ${isMe ? 'justify-end' : ''}`}>
                            <span>{formatTime(msg.created_at)}</span>
                            {isMe && (
                              msg.read 
                                ? <CheckCheck className="w-3 h-3 text-blue-500" />
                                : <Check className="w-3 h-3" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-4 bg-white border-t">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={!newMessage.trim() || sending}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-100">
              <div className="text-center">
                <MessageCircle className="w-20 h-20 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Your Messages</h3>
                <p className="text-slate-500 mb-4">Select a conversation or start a new one</p>
                <Button 
                  onClick={() => {
                    setShowNewChatModal(true);
                    loadParents();
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Conversation
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  New Conversation
                </span>
                <Button variant="ghost" size="sm" onClick={() => setShowNewChatModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Parent</label>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {parents.length === 0 ? (
                    <p className="p-4 text-center text-slate-500">No parents found</p>
                  ) : (
                    parents.map(parent => (
                      <div
                        key={parent.id}
                        onClick={() => setSelectedParent(parent)}
                        className={`p-3 border-b cursor-pointer hover:bg-slate-50 ${
                          selectedParent?.id === parent.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                      >
                        <p className="font-medium">{parent.name}</p>
                        <p className="text-sm text-slate-500">{parent.email}</p>
                        {parent.children?.length > 0 && (
                          <p className="text-xs text-sanjaya-navy">
                            Children: {parent.children.map(c => c.name).join(', ')}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {selectedParent && (
                <div>
                  <label className="block text-sm font-medium mb-2">Initial Message (optional)</label>
                  <Textarea
                    value={initialMessage}
                    onChange={(e) => setInitialMessage(e.target.value)}
                    placeholder="Start the conversation..."
                    rows={3}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowNewChatModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={startNewChat}
                  disabled={!selectedParent || sending}
                >
                  {sending ? 'Starting...' : 'Start Chat'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrincipalChat;
